import {
  cn,
  Icon,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@risksmart-app/atomic-ui';
import type { FC, ReactNode } from 'react';

export interface RiskScoreBreakdownControlTypeRow {
  /** React key when labels may collide (two child risks sharing a title); defaults to `label`. */
  id?: string;
  /** Already-resolved label (e.g. "Preventive" / "Unclassified"). */
  label: string;
  /** Pluralised count copy, e.g. "2 controls". */
  count: string;
  /** Avg-effectiveness display, e.g. "3 avg". */
  value: string;
}

export interface RiskScoreBreakdownDimensionRow {
  /** Dimension label (e.g. "Likelihood"). */
  label: string;
  /** Aggregate avg display (e.g. "avg 2.8" or "avg —"). */
  aggregate: string;
  /** Reduction percent + band display, e.g. "55% (band 2–3)". */
  reduction: string;
}

export interface RiskScoreBreakdownCalculationSection {
  heading: string;
  /** Pre-formatted formula lines, rendered with `font-mono`. */
  lines: string[];
}

export interface RiskScoreBreakdownDetails {
  controlTypesHeading: string;
  /** Column captions above `controlTypes`, for rows whose cells are not self-describing. */
  controlTypesHeaderRow?: RiskScoreBreakdownControlTypeRow;
  controlTypes: RiskScoreBreakdownControlTypeRow[];
  dimensionsHeading: string;
  dimensions: RiskScoreBreakdownDimensionRow[];
  calculation?: {
    summaryLabel: string;
    sections: RiskScoreBreakdownCalculationSection[];
  };
}

export interface RiskScoreBreakdownPopoverProps {
  /** aria-label on the trigger icon button. */
  triggerLabel: string;
  /** Narrative paragraphs rendered above the details section. */
  paragraphs: string[];
  /**
   * Optional structured details. When omitted (e.g. for an `incomplete`
   * breakdown) only the paragraphs render.
   */
  details?: RiskScoreBreakdownDetails;
  /**
   * Optional override for the trigger icon. Defaults to `info-circle`.
   */
  triggerIcon?: ReactNode;
}

/**
 * Both detail blocks are real tables rather than a CSS grid of bare spans, so
 * a screen reader announces "Uncontrolled"/"Controlled" as the column a value
 * sits in and the row's own label as its header. `display: contents` on ARIA
 * rows would have kept the grid, but it drops the element from the
 * accessibility tree in several shipping browsers.
 */
const CAPTION_CLASS =
  'm-0 mb-1 text-left text-sm font-semibold uppercase text-neutral-muted-foreground';
const COLUMN_HEADER_CLASS =
  'text-xs font-semibold uppercase text-neutral-muted-foreground';
const ROW_HEADER_CLASS = 'text-sm font-normal';

const DataRow: FC<{ label: string; count: string; value: string }> = ({
  label,
  count,
  value,
}) => (
  <tr>
    <th scope={'row'} className={cn(ROW_HEADER_CLASS)}>
      {label}
    </th>
    <td className={cn('pl-3 text-sm text-neutral-muted-foreground')}>
      {count}
    </td>
    <td className={cn('pl-3 text-sm tabular-nums')}>{value}</td>
  </tr>
);

const ControlTypeRows: FC<{
  heading: string;
  headerRow?: RiskScoreBreakdownControlTypeRow;
  rows: RiskScoreBreakdownControlTypeRow[];
}> = ({ heading, headerRow, rows }) => {
  if (rows.length === 0) {
    return null;
  }

  return (
    <table className={cn('w-full border-collapse text-left')}>
      <caption className={cn(CAPTION_CLASS)}>{heading}</caption>
      {headerRow && (
        <thead>
          <tr>
            {[headerRow.label, headerRow.count, headerRow.value].map(
              (caption, index) => (
                <th
                  key={index}
                  scope={'col'}
                  className={cn(COLUMN_HEADER_CLASS, index > 0 && 'pl-3')}
                >
                  {caption}
                </th>
              )
            )}
          </tr>
        </thead>
      )}
      <tbody>
        {rows.map((row) => (
          <DataRow
            key={row.id ?? row.label}
            label={row.label}
            count={row.count}
            value={row.value}
          />
        ))}
      </tbody>
    </table>
  );
};

const DimensionRows: FC<{
  heading: string;
  rows: RiskScoreBreakdownDimensionRow[];
}> = ({ heading, rows }) => (
  <table className={cn('w-full border-collapse text-left')}>
    <caption className={cn(CAPTION_CLASS, 'text-xs')}>{heading}</caption>
    <tbody>
      {rows.map((row) => (
        <DataRow
          key={row.label}
          label={row.label}
          count={row.aggregate}
          value={row.reduction}
        />
      ))}
    </tbody>
  </table>
);

const CalculationDisclosure: FC<{
  summaryLabel: string;
  sections: RiskScoreBreakdownCalculationSection[];
}> = ({ summaryLabel, sections }) => (
  <details
    className={cn('group/calc')}
    data-testid={'risk-score-breakdown-calc'}
  >
    <summary
      className={cn(
        'm-0 cursor-pointer list-none text-sm font-semibold uppercase text-neutral-muted-foreground hover:text-foreground'
      )}
    >
      <span
        className={cn(
          'inline-block transition-transform group-open/calc:rotate-90'
        )}
      >
        {'›'}
      </span>{' '}
      {summaryLabel}
    </summary>
    <div className={cn('mt-2 flex flex-col gap-3')}>
      {sections.map((section) => (
        <div key={section.heading} className={cn('flex flex-col gap-0.5')}>
          <p className={cn('m-0 text-sm font-semibold')}>{section.heading}</p>
          {section.lines.map((line, index) => (
            <p
              key={index}
              className={cn(
                'm-0 text-sm font-mono tabular-nums leading-relaxed whitespace-pre-wrap'
              )}
            >
              {line}
            </p>
          ))}
        </div>
      ))}
    </div>
  </details>
);

const Details: FC<{ details: RiskScoreBreakdownDetails }> = ({ details }) => (
  <div
    className={cn('flex flex-col gap-3 pt-2 border-t border-neutral-border')}
    data-testid={'risk-score-breakdown-details'}
  >
    <ControlTypeRows
      heading={details.controlTypesHeading}
      headerRow={details.controlTypesHeaderRow}
      rows={details.controlTypes}
    />
    <DimensionRows
      heading={details.dimensionsHeading}
      rows={details.dimensions}
    />
    {details.calculation && (
      <CalculationDisclosure
        summaryLabel={details.calculation.summaryLabel}
        sections={details.calculation.sections}
      />
    )}
  </div>
);

/**
 * The popover body — paragraphs + optional details — rendered without a
 * `<Popover>` wrapper. Exposed so consumers can host the same content in
 * a different surface (e.g. a side panel) and so the layout branches are
 * testable without exercising a portalled popover.
 */
const RiskScoreBreakdownPopoverContent: FC<{
  paragraphs: string[];
  details?: RiskScoreBreakdownDetails;
}> = ({ paragraphs, details }) => (
  <div className={cn('flex flex-col gap-3 text-base')}>
    <div className={cn('flex flex-col gap-2')}>
      {paragraphs.map((paragraph, index) => (
        <p key={index} className={cn('m-0 leading-snug')}>
          {paragraph}
        </p>
      ))}
    </div>
    {details && <Details details={details} />}
  </div>
);

const RiskScoreBreakdownPopover: FC<RiskScoreBreakdownPopoverProps> = ({
  triggerLabel,
  paragraphs,
  details,
  triggerIcon,
}) => (
  <Popover>
    <PopoverTrigger
      render={(triggerProps) => (
        <IconButton
          {...triggerProps}
          aria-label={triggerLabel}
          data-testid={'risk-score-breakdown-trigger'}
        >
          {triggerIcon ?? <Icon name={'info-circle'} size={'sm'} />}
        </IconButton>
      )}
    />
    <PopoverContent
      className={cn('max-w-md p-4')}
      data-testid={'risk-score-breakdown-content'}
    >
      <RiskScoreBreakdownPopoverContent
        paragraphs={paragraphs}
        details={details}
      />
    </PopoverContent>
  </Popover>
);

export { RiskScoreBreakdownPopover, RiskScoreBreakdownPopoverContent };
